import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, MessageSquareText, Table2, ThermometerSun } from "lucide-react";
import type { AnalyticsFilterDto } from "@hotel-surveys/shared";
import { api, type Hotel, type Location, type QrRoute, type SubLocation } from "../api/client";
import {
  analyticsQueryString,
  useAnalyticsComments,
  useAnomalies,
  useCorrelation,
  useCsatTrend,
  useHeatmap,
  useKeywordClusters,
  useLowScoreOverview,
  useLowScoreReasons,
  useNpsTrend,
  useRedFlags,
  useReviewCta,
  useServiceRecovery,
  useSlaTtr
} from "../api/analytics";

export function AnalyticsPage({ hotelId = "" }: { hotelId?: string }) {
  const [filters, setFilters] = useState<AnalyticsFilterDto>({ hotelId: "", locationId: "", subLocationId: "", qrRouteId: "", unitCode: "", language: "", mealPeriod: "", staffShiftId: "", questionCategory: "", deviceType: "", requestPath: "" });
  useEffect(() => {
    setFilters((current) => ({ ...current, hotelId, locationId: "", subLocationId: "", qrRouteId: "" }));
  }, [hotelId]);
  const queryString = useMemo(() => analyticsQueryString(filters), [filters]);
  const querySuffix = queryString ? `?${queryString}` : "";
  const hotels = useMetaQuery<Hotel[]>("hotels", "/api/admin/hotels");
  const locations = useMetaQuery<Location[]>("locations", "/api/admin/locations");
  const subLocations = useMetaQuery<SubLocation[]>("sub-locations", "/api/admin/sub-locations");
  const qrRoutes = useMetaQuery<QrRoute[]>("qr-routes", "/api/admin/qr-routes");
  const scopedLocations = (locations.data ?? []).filter((location) => !filters.hotelId || location.hotelId === filters.hotelId);
  const scopedSubLocations = (subLocations.data ?? []).filter((subLocation) => (!filters.locationId || subLocation.locationId === filters.locationId) && (!filters.hotelId || subLocation.hotelId === filters.hotelId));
  const nps = useNpsTrend(filters);
  const csat = useCsatTrend(filters);
  const heatmap = useHeatmap(filters);
  const comments = useAnalyticsComments(filters);
  const redFlags = useRedFlags(filters);
  const lowScore = useLowScoreOverview(filters);
  const lowScoreReasons = useLowScoreReasons(filters);
  const serviceRecovery = useServiceRecovery(filters);
  const correlations = useCorrelation(filters);
  const anomalies = useAnomalies(filters);
  const slaTtr = useSlaTtr(filters);
  const keywordClusters = useKeywordClusters(filters);
  const reviewCta = useReviewCta(filters);

  return (
    <div className="page-stack analytics-page">
      <section className="tool-hero card analytics-hero">
        <div>
          <span className="panel-eyebrow left">Analytics</span>
          <h2>NPS, CSAT, komentari i red flag trendovi po scoped objektima.</h2>
          <p>Analitika koristi stable question_key i option value; CSV export ne dekriptuje tekstualne PII odgovore.</p>
        </div>
        <div className="object-brand-card">
          <ThermometerSun size={34} />
          <strong>{heatmap.data?.[0]?.location ?? "Heatmap"}</strong>
          <span>Najkriticnija lokacija po CSAT vrijednosti</span>
        </div>
      </section>

      <section className="kpis">
        <AnalyticsKpi label="NPS uzorak" value={(nps.data ?? []).reduce((sum, item) => sum + item.total, 0).toString()} />
        <AnalyticsKpi label="CSAT uzorak" value={(csat.data ?? []).reduce((sum, item) => sum + item.total, 0).toString()} />
        <AnalyticsKpi label="Komentari" value={(comments.data?.length ?? 0).toString()} />
        <AnalyticsKpi label="Red flag" value={(redFlags.data?.length ?? 0).toString()} />
      </section>

      <section className="kpis">
        <AnalyticsKpi label="Low-score rate" value={`${(lowScore.data?.lowScoreRate ?? 0).toFixed(1)}%`} />
        <AnalyticsKpi label="Follow-up" value={(lowScore.data?.followUpCompleted ?? 0).toString()} />
        <AnalyticsKpi label="Comment rate" value={`${(lowScore.data?.commentRate ?? 0).toFixed(1)}%`} />
        <AnalyticsKpi label="Contact rate" value={`${(lowScore.data?.contactPermissionRate ?? 0).toFixed(1)}%`} />
      </section>

      <section className="kpis">
        <AnalyticsKpi label="Review modal shown" value={(reviewCta.data?.shown ?? 0).toString()} />
        <AnalyticsKpi label="Review clicks" value={(reviewCta.data?.clicked ?? 0).toString()} />
        <AnalyticsKpi label="Review CTR" value={`${(reviewCta.data?.clickThroughRate ?? 0).toFixed(1)}%`} />
        <AnalyticsKpi label="Review lokacije" value={(reviewCta.data?.byLocation.length ?? 0).toString()} />
      </section>

      <section className="panel card">
        <div className="panel-header compact">
          <div>
            <h2>Filteri</h2>
            <p>Filtriranje po hotelu, lokaciji, sublokaciji, jeziku, uredjaju i originalnom QR path-u.</p>
          </div>
        </div>
        <div className="inline-form">
          <select value={filters.hotelId} onChange={(event) => setFilters((current) => ({ ...current, hotelId: event.target.value, locationId: "", subLocationId: "" }))}>
            <option value="">Svi hoteli</option>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <select value={filters.locationId} onChange={(event) => setFilters((current) => ({ ...current, locationId: event.target.value, subLocationId: "" }))}>
            <option value="">Sve lokacije</option>
            {scopedLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
          <select value={filters.subLocationId} onChange={(event) => setFilters((current) => ({ ...current, subLocationId: event.target.value }))}>
            <option value="">Sve sublokacije</option>
            {scopedSubLocations.map((subLocation) => <option key={subLocation.id} value={subLocation.id}>{subLocation.location.name} - {subLocation.name}</option>)}
          </select>
          <select value={filters.language} onChange={(event) => setFilters((current) => ({ ...current, language: event.target.value }))}>
            <option value="">Svi jezici</option>
            <option value="sr-Latn">sr-Latn</option>
            <option value="en">en</option>
            <option value="ru">ru</option>
            <option value="de">de</option>
            <option value="fr">fr</option>
            <option value="es">es</option>
            <option value="pt">pt</option>
            <option value="he">he</option>
          </select>
          <select value={filters.deviceType} onChange={(event) => setFilters((current) => ({ ...current, deviceType: event.target.value }))}>
            <option value="">Svi uredjaji</option>
            <option value="mobile">mobile</option>
            <option value="tablet">tablet</option>
            <option value="desktop">desktop</option>
            <option value="unknown">unknown</option>
          </select>
          <input value={filters.requestPath} onChange={(event) => setFilters((current) => ({ ...current, requestPath: event.target.value }))} placeholder="/br/sp/r1" />
          <select value={filters.qrRouteId} onChange={(event) => setFilters((current) => ({ ...current, qrRouteId: event.target.value }))}>
            <option value="">Sve QR rute</option>
            {(qrRoutes.data ?? []).map((route) => <option key={route.id} value={route.id}>{route.shortPath} - {route.label}</option>)}
          </select>
          <input value={filters.unitCode} onChange={(event) => setFilters((current) => ({ ...current, unitCode: event.target.value }))} placeholder="Soba/sto/unit" />
          <select value={filters.mealPeriod} onChange={(event) => setFilters((current) => ({ ...current, mealPeriod: event.target.value }))}>
            <option value="">Svi meal periodi</option>
            <option value="breakfast">breakfast</option>
            <option value="lunch">lunch</option>
            <option value="dinner">dinner</option>
            <option value="outside_window">outside_window</option>
          </select>
          <input value={filters.staffShiftId} onChange={(event) => setFilters((current) => ({ ...current, staffShiftId: event.target.value }))} placeholder="Shift ID" />
          <input value={filters.questionCategory} onChange={(event) => setFilters((current) => ({ ...current, questionCategory: event.target.value }))} placeholder="Kategorija pitanja" />
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Correlation Engine</h2>
              <p>Pearson korelacija izmedju Time to Acknowledge i CSAT/NPS skora.</p>
            </div>
            <Table2 size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Segment</th><th>Metric</th><th>r</th><th>Sample</th><th>Snaga</th></tr></thead>
              <tbody>
                {(correlations.data ?? []).slice(0, 10).map((row) => (
                  <tr key={`${row.segment}-${row.metricY}`}>
                    <td>{row.segment}</td>
                    <td>{row.metricX} to {row.metricY}</td>
                    <td>{row.coefficient.toFixed(3)}</td>
                    <td>{row.sampleSize}</td>
                    <td>{row.strength}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Anomaly Detection</h2>
              <p>CSAT pad veći od 15% ispod 30-day moving average po lokaciji i meal periodu.</p>
            </div>
            <ThermometerSun size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Lokacija</th><th>Meal</th><th>Current</th><th>30d avg</th><th>Delta</th></tr></thead>
              <tbody>
                {(anomalies.data ?? []).slice(0, 10).map((row) => (
                  <tr key={`${row.hotel}-${row.location}-${row.mealPeriod ?? ""}`}>
                    <td>{row.hotel} - {row.location}</td>
                    <td>{row.mealPeriod ?? "-"}</td>
                    <td>{row.currentCsat.toFixed(2)}</td>
                    <td>{row.movingAverage30d.toFixed(2)}</td>
                    <td>{row.deltaPercent.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>SLA & TTR</h2>
              <p>Time to Resolution i boolean breach flagovi za red flag incidente.</p>
            </div>
            <Table2 size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Incident</th><th>Status</th><th>TTA</th><th>TTR</th><th>SLA</th></tr></thead>
              <tbody>
                {(slaTtr.data ?? []).slice(0, 10).map((row) => (
                  <tr key={row.incidentId}>
                    <td>{row.incidentNumber}<span>{row.hotel} - {row.location}</span></td>
                    <td>{row.status}</td>
                    <td>{formatMinutes(row.timeToAcknowledgeMinutes)}</td>
                    <td>{formatMinutes(row.timeToResolutionMinutes)}</td>
                    <td>{row.acknowledgeSlaBreached || row.resolutionSlaBreached ? "breach" : "ok"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>NLP Keyword Clustering</h2>
              <p>Lokalno grupisanje recurring negative/passive keyword-a iz komentara.</p>
            </div>
            <MessageSquareText size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Cluster</th><th>Keyword</th><th>Broj</th></tr></thead>
              <tbody>
                {(keywordClusters.data ?? []).slice(0, 12).map((row) => (
                  <tr key={`${row.cluster}-${row.keyword}`}>
                    <td>{row.cluster}</td>
                    <td>{row.keyword}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Low-score razlozi</h2>
              <p>Agregacija koristi stable reason value, nezavisno od jezika.</p>
            </div>
            <Table2 size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Reason value</th>
                  <th>Broj</th>
                </tr>
              </thead>
              <tbody>
                {(lowScoreReasons.data ?? []).slice(0, 12).map((row) => (
                  <tr key={row.key}>
                    <td>{row.key}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Service recovery funnel</h2>
              <p>Low score do komentara, kontakta i incident statusa.</p>
            </div>
            <ThermometerSun size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Korak</th>
                  <th>Broj</th>
                </tr>
              </thead>
              <tbody>
                {(serviceRecovery.data ?? []).map((row) => (
                  <tr key={row.step}>
                    <td>{humanizeStep(row.step)}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Heatmap</h2>
              <p>Najlosije lokacije i kategorije su prve.</p>
            </div>
            <Table2 size={20} />
          </div>
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Lokacija</th>
                  <th>Sublokacija</th>
                  <th>Kategorija</th>
                  <th>CSAT</th>
                  <th>Red flag</th>
                </tr>
              </thead>
              <tbody>
                {(heatmap.data ?? []).slice(0, 12).map((row) => (
                  <tr key={`${row.hotel}-${row.location}-${row.subLocation ?? ""}-${row.category}`}>
                    <td>{row.hotel}</td>
                    <td>{row.location}</td>
                    <td>{row.subLocation ?? "-"}</td>
                    <td>{row.category}</td>
                    <td>{row.csat.toFixed(2)}</td>
                    <td>{row.redFlagRate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Komentari</h2>
              <p>Prikaz je PII-redacted; tekstualni CSV export ostaje encrypted marker.</p>
            </div>
            <MessageSquareText size={20} />
          </div>
          <div className="comment-list">
            {(comments.data ?? []).slice(0, 8).map((comment) => (
              <article className="comment-card" key={comment.id}>
                <strong>{comment.hotel} - {comment.location}</strong>
                <span>{comment.subLocation ?? comment.unitCode ?? ""} {comment.requestPath ? `- ${comment.requestPath}` : ""} {comment.deviceType ? `- ${comment.deviceType}` : ""}</span>
                <small>{comment.questionKey}</small>
                {comment.commentRedacted ? (
                  <p className="status">Komentar je sakriven: nalog nema pii.view_comment.</p>
                ) : (
                  <p>{comment.comment}</p>
                )}
              </article>
            ))}
          </div>
        </section>
      </div>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Export</h2>
            <p>Export se audit-loguje i ne sadrzi dekriptovane tekstualne odgovore.</p>
          </div>
          <a className="button-link" href={`/api/admin/analytics/export.csv${querySuffix}`}><Download size={16} /> CSV export</a>
        </div>
      </section>
    </div>
  );
}

function AnalyticsKpi({ label, value }: { label: string; value: string }) {
  return (
    <article className="kpi card">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function humanizeStep(value: string) {
  return value.replace(/_/g, " ");
}

function formatMinutes(value: number | null) {
  return value === null ? "-" : `${value.toFixed(1)}m`;
}

function useMetaQuery<T>(key: string, path: string) {
  return useQuery({ queryKey: [key], queryFn: () => api<T>(path) });
}
